import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, brl, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, ShoppingBag, FileSignature, Wallet, History, ChevronDown, ChevronUp, Send, Receipt, FileText, AlertTriangle, CheckCircle2, Clock, Banknote, QrCode, MessageCircle, Loader2 } from "lucide-react";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  vencida: { dot: "🔴", cls: "bg-red-50 text-red-700" },
  parcial: { dot: "🟡", cls: "bg-amber-50 text-amber-700" },
  em_dia: { dot: "🟢", cls: "bg-emerald-50 text-emerald-700" },
  quitado: { dot: "🟢", cls: "bg-emerald-50 text-emerald-700" },
  sem_compras: { dot: "⚪", cls: "bg-gray-100 text-gray-500" },
};
const TABS = [["resumo", "Resumo", User], ["compras", "Compras", ShoppingBag], ["promissorias", "Promissórias", FileSignature], ["pagamentos", "Pagamentos", Wallet], ["historico", "Histórico", History]];

export default function EletrosBuyerProfile({ admin = false, basePath }) {
  const { clientId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("resumo");
  const [resending, setResending] = useState({});
  const listPath = basePath || (admin ? "/admin/eletros/compradores" : "/revenda/eletros/compradores");

  const load = async () => {
    try {
      const params = admin ? { scope: "admin" } : {};
      const { data } = await api.get(`/eletros/buyers/${clientId}/profile`, { params });
      setData(data);
    } catch (e) {
      setErr(e.response?.status === 403 ? "Este comprador não pertence à sua carteira Eletros." : "Comprador não encontrado.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [clientId]);

  const resend = async (saleId) => {
    if (resending[saleId]) return;
    setResending((p) => ({ ...p, [saleId]: true }));
    try { const { data } = await api.post(`/eletros/sales/${saleId}/resend-signature`); toast.success(data.message || "Reenviado pelo WhatsApp."); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao reenviar"); }
    finally { setTimeout(() => setResending((p) => ({ ...p, [saleId]: false })), 4000); }
  };

  if (err) return (
    <div className="p-6" data-testid="eletros-buyer-error">
      <button onClick={() => nav(listPath)} className="mb-4 flex items-center gap-1 text-sm font-semibold text-[#5B2CA0]"><ArrowLeft size={15} /> Voltar</button>
      <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">{err}</div>
    </div>
  );
  if (!data) return <div className="p-6 text-sm text-[#9A8A98]">Carregando perfil...</div>;

  const { buyer, summary } = data;
  const st = STATUS_STYLE[summary.status.code] || STATUS_STYLE.sem_compras;

  return (
    <div className="space-y-5 p-4 lg:p-8" data-testid="eletros-buyer-profile">
      <button onClick={() => nav(listPath)} className="flex items-center gap-1 text-sm font-semibold text-[#5B2CA0]" data-testid="buyer-back-btn"><ArrowLeft size={15} /> Voltar aos compradores</button>

      {/* Cabecalho premium */}
      <div className="rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }} data-testid="buyer-header">
        <div className="flex items-start gap-3">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl" style={{ background: GOLD }}><User size={28} className="text-[#2B1E4A]" /></div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl font-bold" data-testid="buyer-name">{buyer.name}</h1>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Comprador Eletros</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-white/80">
              {buyer.reseller_name && <span>Revendedora: <b className="text-white">{buyer.reseller_name}</b></span>}
              <span>Telefone: {buyer.phone || "—"}</span>
              <span>CPF: {buyer.cpf || "—"}</span>
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${st.cls}`} data-testid="buyer-status-badge">{st.dot} {summary.status.label}</span>
        </div>
      </div>

      {/* Alerta de pendencia de assinatura */}
      {summary.promissories_pending > 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="buyer-pending-alert">
          <div className="flex items-center gap-2 text-sm font-bold text-amber-800">
            <AlertTriangle size={18} />
            {summary.promissories_pending === 1 ? "Promissória pendente — 1 compra aguardando assinatura" : `${summary.promissories_pending} promissórias aguardando assinatura`}
          </div>
          <button onClick={() => setTab("promissorias")} className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-[12px] font-bold text-white" data-testid="buyer-pending-view">Ver pendência</button>
        </div>
      ) : summary.purchases > 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2 text-[13px] font-semibold text-emerald-700" data-testid="buyer-prom-ok"><CheckCircle2 size={15} /> Promissórias em dia</div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6" data-testid="buyer-kpis">
        <Kpi label="Total comprado" value={brl(summary.total_bought)} />
        <Kpi label="Total pago" value={brl(summary.total_paid)} gold />
        <Kpi label="Saldo em aberto" value={brl(summary.open_balance)} danger={summary.open_balance > 0} />
        <Kpi label="Compras" value={summary.purchases} />
        <Kpi label="Parcelas em aberto" value={summary.open_installments} />
        <Kpi label="Parcelas vencidas" value={summary.overdue_installments} danger={summary.overdue_installments > 0} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-border pb-px" data-testid="buyer-tabs">
        {TABS.map(([v, label, Icon]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`buyer-tab-${v}`}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-bold transition-colors ${tab === v ? "border-[#5B2CA0] text-[#5B2CA0]" : "border-transparent text-[#8A7788] hover:text-[#5B2CA0]"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "resumo" && <TabResumo data={data} />}
      {tab === "compras" && <TabCompras data={data} resend={resend} resending={resending} admin={admin} onChanged={load} />}
      {tab === "promissorias" && <TabPromissorias data={data} resend={resend} resending={resending} />}
      {tab === "pagamentos" && <TabPagamentos data={data} />}
      {tab === "historico" && <TabHistorico data={data} />}
    </div>
  );
}

function Kpi({ label, value, gold, danger }) {
  const color = danger && value > 0 ? "#DC2626" : gold ? "#C9960A" : PURPLE;
  return (
    <div className="rounded-2xl border border-border bg-white p-3" data-testid={`buyer-kpi-${label}`}>
      <div className="text-[9px] uppercase tracking-widest text-[#9A8A98]">{label}</div>
      <div className="font-display text-lg font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function Prom({ status }) {
  if (status === "SIGNED")
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700" data-testid="prom-signed">✅ Promissória assinada</span>;
  return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="prom-pending">⚠️ Pendente de assinatura</span>;
}

const INST_BADGE = {
  paga: ["🟢 Paga", "bg-emerald-50 text-emerald-700"],
  parcial: ["🟡 Parcial", "bg-amber-50 text-amber-700"],
  a_vencer: ["🔵 A vencer", "bg-sky-50 text-sky-700"],
  vencida: ["🔴 Vencida", "bg-red-50 text-red-700"],
};

function TabResumo({ data }) {
  const { summary, purchases, payments } = data;
  const lastPurchase = purchases[0];
  const lastPayment = payments[0];
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="tab-resumo">
      <div className="rounded-2xl border border-border bg-white p-4">
        <h3 className="mb-2 font-display font-bold text-brand-dark">Promissórias</h3>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><div className="text-[10px] uppercase text-[#9A8A98]">Total</div><div className="text-xl font-bold text-[#5B2CA0]" data-testid="resumo-prom-total">{summary.promissories_total}</div></div>
          <div><div className="text-[10px] uppercase text-[#9A8A98]">Assinadas</div><div className="text-xl font-bold text-emerald-600" data-testid="resumo-prom-signed">{summary.promissories_signed}</div></div>
          <div><div className="text-[10px] uppercase text-[#9A8A98]">Pendentes</div><div className="text-xl font-bold text-amber-600" data-testid="resumo-prom-pending">{summary.promissories_pending}</div></div>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-white p-4">
        <h3 className="mb-2 font-display font-bold text-brand-dark">Destaques</h3>
        <ul className="space-y-1.5 text-[13px]">
          <li className="flex justify-between"><span className="text-[#8A7788]">Última compra</span><span className="font-semibold text-brand-dark">{lastPurchase ? `${formatDate(lastPurchase.created_at)} · ${brl(lastPurchase.total)}` : "—"}</span></li>
          <li className="flex justify-between"><span className="text-[#8A7788]">Último pagamento</span><span className="font-semibold text-brand-dark">{lastPayment ? `${formatDate(lastPayment.paid_at)} · ${brl(lastPayment.amount)}` : "—"}</span></li>
          <li className="flex justify-between"><span className="text-[#8A7788]">Saldo em aberto</span><span className="font-semibold" style={{ color: summary.open_balance > 0 ? "#C9960A" : "#059669" }}>{brl(summary.open_balance)}</span></li>
        </ul>
      </div>
    </div>
  );
}

function TabCompras({ data, resend, resending, admin, onChanged }) {
  const [open, setOpen] = useState({});
  if (!data.purchases.length) return <Empty label="Nenhuma compra Eletros." />;
  return (
    <div className="space-y-3" data-testid="tab-compras">
      {data.purchases.map((p) => (
        <div key={p.id} className={`rounded-2xl border bg-white p-4 ${p.status === "cancelled" ? "border-dashed border-red-200 opacity-80" : "border-border"}`} data-testid={`purchase-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display font-bold text-brand-dark">Compra {p.number ? `#ELT-${p.number}` : p.id.slice(0, 8)} {p.status === "cancelled" && <span className="ml-1 text-[10px] font-bold uppercase text-red-500">Cancelada</span>}</div>
              <div className="text-[12px] text-[#8A7788]">{formatDate(p.created_at)} · {p.payment_method === "cash" ? "À vista" : `${p.installments_count}x`}{p.first_due_date ? ` · 1º venc. ${formatDate(p.first_due_date)}` : ""}</div>
              <div className="mt-1 text-[12px] text-brand-dark">{(p.items || []).map((i) => `${i.name}${i.qty > 1 ? ` (${i.qty})` : ""}`).join(", ")}</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-brand-dark" data-testid={`purchase-total-${p.id}`}>{brl(p.total)}</div>
              <div className="text-[11px] text-[#8A7788]">Pago {brl(p.paid)} · Saldo <b style={{ color: p.balance > 0 ? "#C9960A" : "#059669" }}>{brl(p.balance)}</b></div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Prom status={p.signature_status} />
            {p.signature_status === "SIGNED" && p.promissory_id && (
              <a href={`${BACKEND}/api/promissories/${p.promissory_id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#5B2CA0] hover:underline" data-testid={`purchase-prom-pdf-${p.id}`}><FileText size={13} /> Ver promissória</a>
            )}
            {p.status !== "cancelled" && p.signature_status !== "SIGNED" && (
              <button onClick={() => resend(p.id)} disabled={resending[p.id]} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[12px] font-bold text-[#2B1E4A] disabled:opacity-50" style={{ background: GOLD }} data-testid={`purchase-resend-${p.id}`}><Send size={12} /> {resending[p.id] ? "Reenviando..." : "Reenviar assinatura"}</button>
            )}
            {p.installments.length > 0 && (
              <button onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-[#5B2CA0]" data-testid={`purchase-toggle-${p.id}`}>
                {open[p.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {p.installments.length} parcela(s)
              </button>
            )}
          </div>
          {open[p.id] && (
            <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid={`purchase-installments-${p.id}`}>
              {p.installments.map((it) => <InstallmentRow key={it.id} it={it} admin={admin} onChanged={onChanged} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function InstallmentRow({ it, admin, onChanged }) {
  const [label, cls] = INST_BADGE[it.status] || INST_BADGE.a_vencer;
  const hasCharges = it.fine + it.interest > 0;
  const isPaid = it.status === "paga";
  const [busy, setBusy] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [pixUrl, setPixUrl] = useState("");

  const openPay = () => { setPayAmount(String((it.updated_total ?? it.balance ?? 0).toFixed(2))); setShowPay(true); };

  const confirmPay = async () => {
    const amount = Math.round(parseFloat(String(payAmount).replace(",", ".")) * 100) / 100;
    if (!amount || amount <= 0) { toast.error("Informe um valor válido"); return; }
    setBusy("pay");
    try {
      await api.post("/eletros/payments", { installment_id: it.id, amount, method: "manual" });
      toast.success("Baixa registrada");
      setShowPay(false); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar baixa"); }
    finally { setBusy(""); }
  };

  const genPix = async () => {
    setBusy("pix");
    try {
      const { data } = await api.post(`/eletros/installments/${it.id}/charge`);
      const url = data.ticket_url || data.point_of_interaction?.transaction_data?.ticket_url;
      if (url) { setPixUrl(url); window.open(url, "_blank"); }
      toast.success("PIX gerado (cobra o saldo atualizado)");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar PIX"); }
    finally { setBusy(""); }
  };

  const chargeWa = async () => {
    setBusy("wa");
    try {
      await api.post(`/eletros/installments/${it.id}/whatsapp-charge`);
      toast.success("Cobrança enviada por WhatsApp");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao cobrar via WhatsApp"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-border p-3" data-testid={`installment-${it.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-brand-dark">Parcela {it.number} <span className="text-[11px] font-normal text-[#8A7788]">· venc. {formatDate(it.due_date)}</span></div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid={`installment-status-${it.id}`}>{label}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[12px] sm:grid-cols-4">
        <Row k="Principal" v={brl(it.balance > 0 ? it.balance : 0)} />
        <Row k="Valor original" v={brl(it.original)} />
        <Row k="Pago" v={brl(it.paid_total)} />
        <Row k="Saldo restante" v={brl(it.balance)} />
      </div>
      {hasCharges && (
        <div className="mt-2 rounded-lg bg-red-50 p-2 text-[12px]" data-testid={`installment-charges-${it.id}`}>
          <Row k="Multa" v={brl(it.fine)} />
          <Row k="Juros" v={brl(it.interest)} />
          <div className="mt-1 flex justify-between border-t border-red-200 pt-1 font-bold text-red-700"><span>Total atualizado</span><span>{brl(it.updated_total)}</span></div>
          {it.overdue_days > 0 && <div className="mt-0.5 text-[11px] text-red-600">{it.overdue_days} dia(s) em atraso</div>}
        </div>
      )}

      {!isPaid && !showPay && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`installment-actions-${it.id}`}>
          {admin && (
            <button onClick={openPay} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[12px] font-bold text-[#2B1E4A] disabled:opacity-50" style={{ background: GOLD }} data-testid={`inst-pay-${it.id}`}><Banknote size={13} /> Marcar como pago</button>
          )}
          <button onClick={genPix} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg border border-[#2B1E4A] px-2.5 py-1.5 text-[12px] font-bold text-[#2B1E4A] disabled:opacity-50" data-testid={`inst-pix-${it.id}`}>{busy === "pix" ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />} Gerar PIX</button>
          <button onClick={chargeWa} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 px-2.5 py-1.5 text-[12px] font-bold text-emerald-700 disabled:opacity-50" data-testid={`inst-wa-${it.id}`}>{busy === "wa" ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />} Cobrar WhatsApp</button>
          {pixUrl && <a href={pixUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#5B2CA0] hover:underline" data-testid={`inst-pix-link-${it.id}`}>Abrir link PIX</a>}
        </div>
      )}

      {showPay && (
        <div className="mt-3 rounded-lg border border-[#F5B400] bg-[#FFFBEF] p-3" data-testid={`inst-pay-confirm-${it.id}`}>
          <div className="text-[12px] font-bold text-brand-dark">Confirmar baixa manual</div>
          <div className="mt-1 text-[11px] text-[#8A7788]">Saldo atualizado: <b>{brl(it.updated_total ?? it.balance)}</b>. Informe o valor recebido (integral ou parcial).</div>
          <div className="mt-2 flex items-center gap-2">
            <input value={payAmount} onChange={(e) => setPayAmount(e.target.value)} inputMode="decimal" className="w-32 rounded-lg border border-border px-2 py-1.5 text-sm" data-testid={`inst-pay-amount-${it.id}`} />
            <button onClick={confirmPay} disabled={busy === "pay"} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-50" style={{ background: "#059669" }} data-testid={`inst-pay-confirm-btn-${it.id}`}>{busy === "pay" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Confirmar</button>
            <button onClick={() => setShowPay(false)} className="rounded-lg border border-border px-3 py-1.5 text-[12px] font-bold text-[#6B5A69]" data-testid={`inst-pay-cancel-${it.id}`}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TabPromissorias({ data, resend, resending }) {
  const { promissories, purchases } = data;
  // Reune promissorias reais; se nao houver registro documental, usa a venda (signature_status) como fallback de exibicao.
  const items = promissories.length ? promissories.map((p) => ({ ...p, _sale: purchases.find((x) => x.id === p.sale_id) }))
    : purchases.map((s) => ({ id: null, sale_id: s.id, sale_number: s.number, created_at: s.created_at, total: s.total, installments: s.installments_count, signature_status: s.signature_status, reseller_name: data.buyer.reseller_name, _sale: s }));
  if (!items.length) return <Empty label="Nenhuma promissória." />;
  return (
    <div className="space-y-3" data-testid="tab-promissorias">
      {items.map((p, idx) => {
        const sale = p._sale || {};
        return (
          <div key={p.id || p.sale_id || idx} className="rounded-2xl border border-border bg-white p-4" data-testid={`promissory-${p.sale_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold text-brand-dark">Compra {p.sale_number ? `#ELT-${p.sale_number}` : (p.sale_id || "").slice(0, 8)}</div>
                <div className="text-[12px] text-[#8A7788]">{formatDate(p.created_at)} · {brl(p.total)} · {p.installments}x{p.reseller_name ? ` · ${p.reseller_name}` : ""}</div>
                {p.signed_at && <div className="text-[11px] text-emerald-600">Assinada em {formatDate(p.signed_at)}</div>}
              </div>
              <Prom status={p.signature_status} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.signature_status === "SIGNED" && (sale.promissory_id || p.id) && (
                <a href={`${BACKEND}/api/promissories/${sale.promissory_id || p.id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-[#5B2CA0] px-3 py-1.5 text-[12px] font-semibold text-[#5B2CA0]" data-testid={`promissory-pdf-${p.sale_id}`}><FileText size={13} /> Ver promissória</a>
              )}
              {sale.status !== "cancelled" && p.signature_status !== "SIGNED" && (
                <button onClick={() => resend(p.sale_id)} disabled={resending[p.sale_id]} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-bold text-[#2B1E4A] disabled:opacity-50" style={{ background: GOLD }} data-testid={`promissory-resend-${p.sale_id}`}><Send size={13} /> {resending[p.sale_id] ? "Reenviando..." : "Reenviar para assinatura"}</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabPagamentos({ data }) {
  if (!data.payments.length) return <div data-testid="tab-pagamentos"><Empty label="Nenhum pagamento registrado." /></div>;
  const METHOD = { mercado_pago: "Mercado Pago", pix: "PIX", manual: "Baixa manual" };
  return (
    <div className="space-y-2" data-testid="tab-pagamentos">
      {data.payments_total > data.payments.length && (
        <div className="rounded-lg bg-[#FBFAFE] px-3 py-1.5 text-[11px] text-[#8A7788]">Mostrando os {data.payments.length} pagamentos mais recentes de {data.payments_total}.</div>
      )}
      {data.payments.map((p) => (
        <div key={p.id} className="rounded-xl border border-border bg-white p-3" data-testid={`payment-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-brand-dark">{brl(p.amount)} <span className="text-[11px] font-normal text-[#8A7788]">· {formatDate(p.paid_at)}</span></div>
              <div className="text-[12px] text-[#8A7788]">
                Parcela {p.installment_number ?? "?"}{p.due_date ? ` · venc. ${formatDate(p.due_date)}` : ""} · {METHOD[p.method] || p.method || "—"} · {p.automatic ? "Automático" : "Manual"}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.is_partial ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`payment-kind-${p.id}`}>{p.is_partial ? "Pagamento parcial" : "Integral"}</span>
              {p.receipt_token && (
                <a href={`${BACKEND}/api/eletros/receipts/${p.receipt_token}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#5B2CA0] hover:underline" data-testid={`payment-receipt-${p.id}`}>🧾 Ver recibo</a>
              )}
            </div>
          </div>
          {p.is_partial && (
            <div className="mt-2 rounded-lg bg-[#FBFAFE] p-2 text-[12px]">
              <Row k="Valor recebido" v={brl(p.amount)} />
              <Row k="Saldo restante" v={brl(p.balance_after)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const TL_ICON = {
  purchase: "🛒", promissory_sent: "📄", promissory_signed: "✍️",
  payment: "💰", payment_partial: "💰", receipt: "🧾", cancel: "❌", reversal: "↩️",
};

function TabHistorico({ data }) {
  if (!data.timeline.length) return <Empty label="Sem eventos registrados." />;
  return (
    <div className="space-y-0" data-testid="tab-historico">
      {data.timeline.map((e, i) => (
        <div key={i} className="flex gap-3" data-testid={`timeline-event-${i}`}>
          <div className="flex flex-col items-center">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F1ECF7] text-base">{TL_ICON[e.type] || "•"}</div>
            {i < data.timeline.length - 1 && <div className="w-px flex-1 bg-border" />}
          </div>
          <div className="pb-4">
            <div className="text-[11px] text-[#9A8A98]">{formatDate(e.date)}</div>
            <div className="text-[13px] font-semibold text-brand-dark">{e.label}{e.amount != null ? ` · ${brl(e.amount)}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-2"><span className="text-[#8A7788]">{k}</span><span className="font-medium text-brand-dark">{v}</span></div>;
}
function Empty({ label }) {
  return <div className="rounded-2xl border border-dashed border-[#D9CFE8] bg-[#FAF8FD] p-8 text-center text-sm text-[#6B5A69]">{label}</div>;
}
